/**
 * Theme filter component with badge selection and OR/AND mode
 * Displays themes with their colours as left border
 * Gets enriched colours from activities context
 */

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useActivities } from '@/contexts/ActivitiesContext';
import { Tag } from 'lucide-react';
import { FilterModeToggle } from './filter-mode-toggle';

interface Theme {
  id: string;
  name: string;
}

interface FilterThemesProps {
  themes: Theme[];
  selectedThemes: string[];
  filterMode: 'OR' | 'AND';
  onToggleTheme: (themeId: string) => void;
  onFilterModeChange: (mode: 'OR' | 'AND') => void;
  onClearSelection?: () => void;
}

export function FilterThemes({
  themes,
  selectedThemes,
  filterMode,
  onToggleTheme,
  onFilterModeChange,
  onClearSelection,
}: FilterThemesProps) {
  // Get activities from context to access colors directly
  const { getActivityByName } = useActivities();

  if (themes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="theme-filter-mode">
        <Tag className="size-4" /> Themes
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        {themes.map(theme => {
          const isSelected = selectedThemes.includes(theme.id);
          // Get activity from context (already contains color)
          const activity = getActivityByName(theme.name);
          const themeColor = activity?.color;

          return (
            <Badge
              key={theme.id}
              variant="outline"
              className={`cursor-pointer transition-all ${
                isSelected
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'opacity-60 hover:opacity-100'
              }`}
              style={
                themeColor
                  ? {
                      borderColor: themeColor,
                    }
                  : undefined
              }
              onClick={() => onToggleTheme(theme.id)}
            >
              {theme.name}
            </Badge>
          );
        })}
        {selectedThemes.length > 1 && (
          <FilterModeToggle
            filterMode={filterMode}
            onFilterModeChange={onFilterModeChange}
            onClearSelection={onClearSelection}
            isClearDisabled={selectedThemes.length === 0}
            clearAriaLabel="Clear selected themes"
            orTooltip="Show items matching ANY selected option"
            andTooltip="Show items matching ALL selected options"
            switchId="theme-filter-mode"
          />
        )}
      </div>
    </div>
  );
}
